import { useFormContext, useWatch } from "react-hook-form";
import Button from "../../atoms/button";
import Input from "../../atoms/input";
import SelectBox from "../../atoms/select";
import type { SignUpInput } from "../../../types/auth.types";
import { cn } from "../../../lib/utils/cn";
import React from "react";
import { getKoreanId7thDigit } from "../../../lib/utils/birthdayUtils";
import { TELECOM_MENU } from "../../../lib/constants/common.constants";
import { useMutation } from "@tanstack/react-query";
import type { JoinOTPRequest } from "../../../@apis/dto/request/auth.request";
import { joinRequestOtp } from "../../../@apis/v1/auth.service";
import type { AuthCertificationResponse } from "../../../@apis/dto/response/auth.response";
import { useModalStore } from "../../../lib/store/useModalStore";
import type {
  ErrorResponse,
  OnErrorResponse,
} from "../../../@apis/dto/response/common.response";
import { useNativeOtpAutofill } from "../../../hooks/useNativeOtpAutofill";

interface Props {
  onCertification: (item: AuthCertificationResponse) => void;
  /** 인증 요청 완료 여부(탭 이동 시에도 유지되도록 부모에서 관리) */
  otpRequested: boolean;
  /** alert 확인 시 이동/강조할 입력란 키 (name | birthdate | mobileno | otp) */
  focusTarget?: string | null;
  /** focusTarget 처리 완료를 부모에 알림 (재요청 가능하도록 초기화) */
  onFocusHandled?: () => void;
  /** 성별 변경 시 부모에 알림 (변경 여부 비교 등에 사용) */
  onGenderChange?: (gender: "male" | "female") => void;
  /** 내/외국인 변경 시 부모에 알림 */
  onNatChange?: (nat: "domestic" | "foreign") => void;
}

const getRequestOtpErrorMessage = (error: ErrorResponse) => {
  switch (error?.code) {
    case "P016":
      return "이미 가입된 전화번호입니다.";
    case "S007":
      return "본인 정보가 일치하지 않습니다.\n입력하신 내용을 확인 후 다시 시도해 주세요.";
    default:
      return error.message;
  }
};

export default function EnterInfoScreen({
  onCertification,
  otpRequested,
  focusTarget,
  onFocusHandled,
  onGenderChange,
  onNatChange,
}: Props) {
  const { register, setValue, control, watch } = useFormContext<SignUpInput>();
  const { openModal } = useModalStore();
  const { startListen } = useNativeOtpAutofill((otp) => {
    setValue("otp", otp, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  });
  const [gender, setGenderState] = React.useState<"male" | "female">("male");
  const [nat, setNatState] = React.useState<"domestic" | "foreign">("domestic");

  const setGender = (value: "male" | "female") => {
    setGenderState(value);
    onGenderChange?.(value);
  };
  const setNat = (value: "domestic" | "foreign") => {
    setNatState(value);
    onNatChange?.(value);
  };
  // 인증 요청 완료 여부는 부모(otpRequested)에서 관리하여 탭 이동 시에도 유지
  const request = otpRequested;

  const birthdate = useWatch({ control, name: "birthdate" });

  // 입력란 스크롤/강조용 ref 및 상태
  const nameRef = React.useRef<HTMLDivElement>(null);
  const birthdateRef = React.useRef<HTMLDivElement>(null);
  const mobilenoRef = React.useRef<HTMLDivElement>(null);
  const otpRef = React.useRef<HTMLDivElement>(null);
  const authButtonRef = React.useRef<HTMLDivElement>(null);
  const sectionRefs: Record<string, React.RefObject<HTMLDivElement | null>> = {
    name: nameRef,
    birthdate: birthdateRef,
    mobileno: mobilenoRef,
    otp: otpRef,
    certification: authButtonRef,
  };
  const [highlight, setHighlight] = React.useState<string | null>(null);
  // 인증 요청 버튼 상단 안내 문구 노출 여부
  const [showAuthGuide, setShowAuthGuide] = React.useState(false);

  // 인증번호 입력란 포커스
  const focusOtpInput = () => {
    otpRef.current
      ?.querySelector<HTMLElement>("input")
      ?.focus({ preventScroll: false });
  };

  // 부모에서 focusTarget이 지정되면 해당 영역으로 스크롤 이동 및 포커스/강조
  React.useEffect(() => {
    if (!focusTarget) return;
    const section = sectionRefs[focusTarget]?.current;
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "center" });
      if (focusTarget === "certification") {
        // 인증 요청 버튼 영역으로 이동 후 안내 문구 노출
        setShowAuthGuide(true);
      } else {
        section
          .querySelector<HTMLElement>("input, select")
          ?.focus({ preventScroll: true });
        setHighlight(focusTarget);
      }
    }
    onFocusHandled?.();
  }, [focusTarget]);

  // 사용자가 입력란을 수정하기 시작하면 강조 해제
  const clearHighlight = () => {
    if (highlight) setHighlight(null);
  };

  // 포커스 시 기본 검정 테두리와 겹치지 않도록 강조 색상을 우선 적용
  const highlightClass = (field: string) =>
    highlight === field ? "!border-primary !ring-1 !ring-primary" : "";

  // 포커스 색상을 primary로 적용
  const focusClass = "focus:!border-primary focus:!ring-primary";

  // disabled 시 숫자/전화 입력이 파랗게 보이는 현상(iOS -webkit-text-fill-color)
  // 을 막아 일반 input과 동일한 회색으로 통일
  const inputClass = cn(
    "text-[0.7rem] placeholder:text-[0.7rem]",
    "disabled:[-webkit-text-fill-color:#9ca3af]",
    focusClass,
  );

  const disabledAuthButton = () => {
    const [name, birthdate, jumin1, mobileco, mobileno] = watch([
      "name",
      "birthdate",
      "jumin1",
      "mobileco",
      "mobileno",
    ]);
    if (
      name &&
      (birthdate?.length ?? 0) === 6 &&
      jumin1 &&
      mobileco &&
      (mobileno?.length ?? 0) >= 10
    )
      return false;

    return true;
  };

  const joinRequestOTPMutation = useMutation({
    mutationFn: (form: JoinOTPRequest) => joinRequestOtp(form),
    onSuccess: (res) => {
      const { success, data } = res;
      if (success) {
        // 이미 인증 요청한 상태면 재발송
        const isResend = request;
        onCertification(data); // 부모에서 otpRequested(true) 설정
        openModal("ALERT_DIALOG", {
          title: isResend ? "인증번호 재발송 완료" : "인증번호 발송 완료",
          content: isResend
            ? "인증번호가 다시 발송되었습니다.\n인증번호 확인 후 입력해주세요."
            : "인증번호가 발송되었습니다.\n인증번호 6자리 확인 후 입력해주세요.",
          // 확인 클릭 시 인증번호 입력란 포커스
          onConfirm: focusOtpInput,
        });
      }
    },
    onError: (err: OnErrorResponse) => {
      const { error } = err.response.data;

      // 재요청 실패 시에는 고정 안내 문구 노출
      if (request) {
        openModal("ALERT_DIALOG", {
          title: "인증번호 재발송 실패",
          content:
            error?.code == "P018"
              ? "이미 인증번호를 발송하셨습니다.\n3분 후에 다시 시도해 주세요."
              : error?.message,
        });
        return;
      }

      openModal("ALERT_DIALOG", {
        title: "인증번호 발송 실패",
        content: getRequestOtpErrorMessage(error as ErrorResponse),
      });
    },
  });

  const requestOtp = () => {
    // 인증 요청 시 안내 문구 숨김
    setShowAuthGuide(false);
    startListen();
    const [name, birthdate, jumin1, mobileno, mobileco] = watch([
      "name",
      "birthdate",
      "jumin1",
      "mobileno",
      "mobileco",
    ]);
    joinRequestOTPMutation.mutate({
      name,
      birthdate,
      jumin1,
      mobileno,
      mobileco,
    });
  };

  // 생년월일, 성별, 국적이 모두 선택되었을 때만 jumin1 갱신
  React.useEffect(() => {
    if (!birthdate || birthdate.length !== 6 || !gender || !nat) {
      setValue("jumin1", undefined as unknown as number);
      return;
    }
    try {
      const digit = getKoreanId7thDigit(birthdate, gender, nat);
      setValue("jumin1", digit);
    } catch {
      setValue("jumin1", undefined as unknown as number);
    }
  }, [birthdate, gender, nat, setValue]);

  return (
    <div>
      <div className="space-y-[12px]">
        <h1 className="font-semibold">본인인증을 진행해 주세요.</h1>
        <p className="text-xs text-[#8e8e93]">
          정보 입력 후 휴대폰으로 본인 인증을 진행해 주세요.
        </p>
      </div>
      <div className="space-y-[16px] mt-[36px]" onChange={clearHighlight}>
        <div ref={nameRef} className="space-y-[8px]">
          <p className="text-base font-bold">이름</p>
          <Input
            maxLength={20}
            disabled={request}
            className={cn(inputClass, highlightClass("name"))}
            placeholder="이름을 입력해주세요."
            {...register("name")}
          />
        </div>
        <div ref={birthdateRef} className="space-y-[8px]">
          <p className="text-base font-bold">생년월일</p>
          <Input
            inputMode="numeric"
            maxLength={6}
            disabled={request}
            className={cn(inputClass, highlightClass("birthdate"))}
            placeholder="생년월일 6자리(YYMMDD)"
            {...register("birthdate")}
          />
        </div>
        <div className="space-y-[8px]">
          <p className="text-base font-bold">성별</p>
          <div className="flex-row gap-[10px]">
            <Button
              className={cn(
                "w-full h-[50px]",
                gender === "male" && "border border-[#222]",
              )}
              variant={"white"}
              disabled={request}
              label="남성"
              onClick={() => setGender("male")}
            />
            <Button
              className={cn(
                "w-full h-[50px]",
                gender === "female" && "border border-[#222]",
              )}
              variant={"white"}
              disabled={request}
              label="여성"
              onClick={() => setGender("female")}
            />
          </div>
        </div>
        <div className="space-y-[8px]">
          <p className="text-base font-bold">내/외국인</p>
          <div className="flex-row gap-[10px]">
            <Button
              className={cn(
                "w-full h-[50px]",
                nat === "domestic" && "border border-[#222]",
              )}
              variant={"white"}
              disabled={request}
              label="내국인"
              onClick={() => setNat("domestic")}
            />
            <Button
              className={cn(
                "w-full h-[50px]",
                nat === "foreign" && "border border-[#222]",
              )}
              variant={"white"}
              disabled={request}
              label="외국인"
              onClick={() => setNat("foreign")}
            />
          </div>
        </div>
        <div className="space-y-[8px]">
          <p className="text-base font-bold">통신사</p>
          <SelectBox
            className="text-[0.7rem]"
            disabled={request}
            options={TELECOM_MENU}
            {...register("mobileco")}
          />
        </div>
        <div ref={mobilenoRef} className="space-y-[8px]">
          <p className="text-base font-bold">휴대전화번호</p>
          <div className="space-y-[16px] pb-[20px]">
            <Input
              type="tel"
              inputMode="numeric"
              maxLength={11}
              disabled={request}
              placeholder="전화번호 - 없이 입력"
              className={cn(inputClass, highlightClass("mobileno"))}
              {...register("mobileno", {
                onChange: (e) => {
                  e.target.value = e.target.value
                    .replace(/[^0-9]/g, "")
                    .slice(0, 11);
                },
              })}
            />
            {request ? (
              <div
                ref={otpRef}
                className="flex flex-col gap-[16px] items-center"
              >
                <div className="relative w-full">
                  <Input
                    inputMode="numeric"
                    maxLength={6}
                    autoComplete="one-time-code"
                    className={cn("min-w-0", inputClass, highlightClass("otp"))}
                    placeholder="인증번호 6자리 입력"
                    {...register("otp")}
                  />
                </div>
                <Button
                  className="w-full "
                  label="인증 재요청"
                  variant="white"
                  disabled={joinRequestOTPMutation.isPending}
                  onClick={requestOtp}
                />
              </div>
            ) : (
              <div ref={authButtonRef} className="space-y-[8px]">
                {showAuthGuide && (
                  <p className="text-xs text-primary font-[500]">
                    본인인증을 진행해 주세요.
                  </p>
                )}
                <Button
                  className="w-full text-white"
                  variant={disabledAuthButton() ? "gray" : "black"}
                  disabled={disabledAuthButton()}
                  label="인증 요청"
                  onClick={requestOtp}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
